import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sortVariants } from '../lib/sortVariants';
import { formatPaymentMode } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import MultiSelectFilter from './MultiSelectFilter';
import SortDropdown from './SortDropdown';
import api from '../lib/api';

const ReadyToDispatch = ({ user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPaymentModes, setFilterPaymentModes] = useState([]);
  const [filterCreditDays, setFilterCreditDays] = useState([]);
  const [filterEvents, setFilterEvents] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc');
  const [lightboxImg, setLightboxImg] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const canDispatch = ['admin', 'manager', 'inventory_staff'].includes(user?.role);
  const [events, setEvents] = useState([]);

  const loadOrders = async () => {
    try {
      const [ordersRes, custRes, eventsRes] = await Promise.all([
        api.get('/ready-to-dispatch'),
        api.get('/customers'),
        api.get('/events').catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setCustomers(custRes.data);
      setEvents(eventsRes.data || []);
    } catch {
      toast.error('Failed to load dispatch data');
    }
    setLoading(false);
  };

  useEffect(() => { loadOrders(); }, []);

  const handleCreateSalesEntry = (order) => {
    navigate('/create-sales-entry', {
      state: {
        prefillData: {
          entry_type: 'against_so',
          linked_so_id: order.order_id,
          customer_id: order.customer_id,
          items: order.items.map(item => ({
            product_id: item.product_id,
            design_code: item.design_code,
            linked_so_id: order.order_id,
            linked_so_item_id: item.item_id,
            variants: item.variants
              .filter(v => v.can_dispatch > 0)
              .map(v => ({
                size: v.size,
                color: v.color,
                qty: v.can_dispatch,
                can_dispatch: v.can_dispatch,
                wsp: v.wsp || 0,
              })),
          })).filter(i => i.variants.length > 0),
        },
      },
    });
  };

  const paymentModeOptions = [...new Set(orders.map(o => o.payment_mode).filter(Boolean))].sort().map(p => ({ value: p, label: formatPaymentMode(p) }));
  const creditDaysOptions = useMemo(() => {
    const custMap = new Map(customers.map(c => [c.id, c]));
    const days = [...new Set(orders.filter(o => o.payment_mode === 'credit').map(o => custMap.get(o.customer_id)?.credit_days).filter(Boolean))].sort();
    return days.map(d => ({ value: d, label: `${d} days` }));
  }, [orders, customers]);
  const showCreditDaysFilter = filterPaymentModes.includes('credit');

  // Build filter options from data
  const eventOptions = useMemo(() => {
    const eventIds = [...new Set(orders.map(o => o.event_id).filter(Boolean))];
    return eventIds.map(eid => {
      const ev = events.find(e => e.id === eid);
      return { value: eid, label: ev?.name || eid };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [orders, events]);
  const agentOptions = useMemo(() => {
    const agents = {};
    orders.forEach(o => { if (o.agent_id && o.agent_name) agents[o.agent_id] = o.agent_name; });
    return Object.entries(agents).map(([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [orders]);
  const distributorOptions = useMemo(() => {
    const dists = {};
    orders.forEach(o => { if (o.distributor_id && o.distributor_name) dists[o.distributor_id] = o.distributor_name; });
    return Object.entries(dists).map(([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [orders]);
  const stateOptions = useMemo(() =>
    [...new Set(orders.map(o => o.customer_state).filter(Boolean))].sort().map(s => ({ value: s, label: s })),
  [orders]);
  const cityOptions = useMemo(() =>
    [...new Set(orders.map(o => o.customer_city).filter(Boolean))].sort().map(c => ({ value: c, label: c })),
  [orders]);

  const rtdSortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'A to Z (Customer)' },
    { value: 'name_desc', label: 'Z to A (Customer)' },
    { value: 'qty_desc', label: 'Qty Ready: High to Low' },
    { value: 'qty_asc', label: 'Qty Ready: Low to High' },
    { value: 'readiness_desc', label: 'Readiness: High to Low' },
    { value: 'readiness_asc', label: 'Readiness: Low to High' },
  ];

  const filtered = useMemo(() => {
    let list = orders;
    if (filterType === 'full') list = list.filter(o => o.readiness_pct === 100);
    if (filterType === 'partial') list = list.filter(o => o.readiness_pct > 0 && o.readiness_pct < 100);
    if (filterPaymentModes.length) list = list.filter(o => filterPaymentModes.includes(o.payment_mode));
    if (filterCreditDays.length) {
      const custMap = new Map(customers.map(c => [c.id, c]));
      list = list.filter(o => filterCreditDays.includes(custMap.get(o.customer_id)?.credit_days));
    }
    if (filterEvents.length) list = list.filter(o => filterEvents.includes(o.event_id));
    if (filterAgents.length) list = list.filter(o => filterAgents.includes(o.agent_id));
    if (filterDistributors.length) list = list.filter(o => filterDistributors.includes(o.distributor_id));
    if (filterStates.length) list = list.filter(o => filterStates.includes(o.customer_state));
    if (filterCities.length) list = list.filter(o => filterCities.includes(o.customer_city));
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(o =>
        o.so_number?.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.items?.some(i => i.design_code?.toLowerCase().includes(q))
      );
    }
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return (a.days_since_order || 0) - (b.days_since_order || 0);
        case 'name_asc': return (a.customer_name || '').localeCompare(b.customer_name || '');
        case 'name_desc': return (b.customer_name || '').localeCompare(a.customer_name || '');
        case 'qty_desc': return (b.total_qty_ready || 0) - (a.total_qty_ready || 0);
        case 'qty_asc': return (a.total_qty_ready || 0) - (b.total_qty_ready || 0);
        case 'readiness_desc': return (b.readiness_pct || 0) - (a.readiness_pct || 0);
        case 'readiness_asc': return (a.readiness_pct || 0) - (b.readiness_pct || 0);
        default: return (b.days_since_order || 0) - (a.days_since_order || 0);
      }
    });
    return list;
  }, [orders, filterType, filterPaymentModes, filterCreditDays, filterEvents, filterAgents, filterDistributors, filterStates, filterCities, searchTerm, customers, sortBy]);

  const stats = useMemo(() => ({
    total: orders.length,
    fullyReady: orders.filter(o => o.readiness_pct === 100).length,
    partial: orders.filter(o => o.readiness_pct > 0 && o.readiness_pct < 100).length,
    aging: orders.filter(o => o.days_since_order >= 7).length,
    totalPcsReady: orders.reduce((s, o) => s + (o.total_qty_ready || 0), 0),
  }), [orders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ready-to-dispatch-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="rtd-title">Ready to Dispatch</h1>
          <p className="text-sm text-gray-500 mt-1">Orders with stock available for dispatch</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total Orders', value: stats.total, cls: 'text-blue-700 bg-blue-50 border-blue-100' },
          { label: 'Fully Ready', value: stats.fullyReady, cls: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
          { label: 'Partial Stock', value: stats.partial, cls: 'text-amber-700 bg-amber-50 border-amber-100' },
          { label: 'Total Pcs Ready', value: stats.totalPcsReady, cls: 'text-indigo-700 bg-indigo-50 border-indigo-100' },
          { label: 'Aging (7+ days)', value: stats.aging, cls: 'text-red-700 bg-red-50 border-red-100' },
        ].map(s => (
          <Card key={s.label} className={`border ${s.cls}`}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs font-medium mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 space-y-3">
          {/* Row 1: Search + Sort + Type */}
          <div className="flex items-end gap-2 flex-wrap">
            <Input
              placeholder="Search SO#, customer, design..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="h-9 w-52 text-sm"
              data-testid="rtd-search-input"
            />
            <SortDropdown value={sortBy} onChange={setSortBy} options={rtdSortOptions} testId="rtd-sort" />
            {[
              { key: 'all', label: 'All' },
              { key: 'full', label: 'Fully Ready' },
              { key: 'partial', label: 'Partial' },
            ].map(f => (
              <Button key={f.key} variant={filterType === f.key ? 'default' : 'outline'} size="sm" className="h-9"
                onClick={() => setFilterType(f.key)} data-testid={`rtd-filter-${f.key}`}>
                {f.label}
              </Button>
            ))}
          </div>
          {/* Row 2: All dropdown filters in a grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            <div>
              <Label className="text-[10px] text-gray-500 mb-0.5 block">Payment</Label>
              <MultiSelectFilter label="All Payments" options={paymentModeOptions} selected={filterPaymentModes} onChange={(v) => { setFilterPaymentModes(v); if (!v.includes('credit')) setFilterCreditDays([]); }} testId="rtd-filter-payment" />
            </div>
            {showCreditDaysFilter && creditDaysOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">Credit Days</Label>
                <MultiSelectFilter label="All Days" options={creditDaysOptions} selected={filterCreditDays} onChange={setFilterCreditDays} testId="rtd-filter-credit-days" />
              </div>
            )}
            {eventOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">Event</Label>
                <MultiSelectFilter label="All Events" options={eventOptions} selected={filterEvents} onChange={setFilterEvents} testId="rtd-filter-event" />
              </div>
            )}
            {agentOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">Agent</Label>
                <MultiSelectFilter label="All Agents" options={agentOptions} selected={filterAgents} onChange={setFilterAgents} testId="rtd-filter-agent" />
              </div>
            )}
            {distributorOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">Distributor</Label>
                <MultiSelectFilter label="All Distributors" options={distributorOptions} selected={filterDistributors} onChange={setFilterDistributors} testId="rtd-filter-distributor" />
              </div>
            )}
            {stateOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">State</Label>
                <MultiSelectFilter label="All States" options={stateOptions} selected={filterStates} onChange={setFilterStates} testId="rtd-filter-state" />
              </div>
            )}
            {cityOptions.length > 0 && (
              <div>
                <Label className="text-[10px] text-gray-500 mb-0.5 block">City</Label>
                <MultiSelectFilter label="All Cities" options={cityOptions} selected={filterCities} onChange={setFilterCities} testId="rtd-filter-city" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SO #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-center">Ready / Total Items</TableHead>
                <TableHead className="text-center">Qty Ready</TableHead>
                <TableHead className="text-center">Readiness</TableHead>
                <TableHead className="text-center">Days</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    {orders.length === 0 ? 'No orders ready for dispatch.' : 'No orders match your filters.'}
                  </TableCell>
                </TableRow>
              ) : filtered.map(o => (
                <React.Fragment key={o.order_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedOrder(expandedOrder === o.order_id ? null : o.order_id)}
                    data-testid={`rtd-row-${o.order_id}`}
                  >
                    <TableCell className="font-mono font-semibold text-blue-700">{o.so_number}</TableCell>
                    <TableCell>
                      <div className="text-base font-semibold">{o.customer_name}</div>
                      <div className="flex gap-2 text-xs text-gray-400 mt-0.5">
                        {o.agent_name && <span className="text-violet-500">Agent: {o.agent_name}</span>}
                        {o.distributor_name && <span className="text-orange-500">Dist: {o.distributor_name}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-emerald-600 font-bold">{o.ready_items}</span>
                      {o.partial_items > 0 && <span className="text-amber-500 font-bold"> + {o.partial_items}</span>}
                      <span className="text-gray-400"> / {o.total_items}</span>
                    </TableCell>
                    <TableCell className="text-center font-semibold">{o.total_qty_ready} / {o.total_qty_ordered}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${o.readiness_pct === 100 ? 'bg-emerald-500' : o.readiness_pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${o.readiness_pct}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-600">{o.readiness_pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`text-sm font-medium ${o.days_since_order >= 7 ? 'text-red-600' : o.days_since_order >= 3 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {o.days_since_order}d
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      {canDispatch && o.readiness_pct === 100 && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleCreateSalesEntry(o)} data-testid={`dispatch-btn-${o.order_id}`}>
                          Sales Entry
                        </Button>
                      )}
                      {canDispatch && o.readiness_pct > 0 && o.readiness_pct < 100 && (
                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-200"
                          onClick={() => handleCreateSalesEntry(o)} data-testid={`partial-dispatch-btn-${o.order_id}`}>
                          Sales Entry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Expanded Item Details */}
                  {expandedOrder === o.order_id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-gray-50 p-0">
                        <div className="p-4" data-testid={`rtd-detail-${o.order_id}`}>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-center py-2 px-2 font-semibold text-gray-600 w-12">Image</th>
                                <th className="text-left py-2 px-3 font-semibold text-gray-600">Design Code</th>
                                <th className="text-left py-2 px-3 font-semibold text-gray-600">Size</th>
                                <th className="text-left py-2 px-3 font-semibold text-gray-600">Color</th>
                                <th className="text-right py-2 px-3 font-semibold text-gray-600">Ordered</th>
                                <th className="text-right py-2 px-3 font-semibold text-gray-600">Dispatched</th>
                                <th className="text-right py-2 px-3 font-semibold text-gray-600">Remaining</th>
                                <th className="text-right py-2 px-3 font-semibold text-gray-600">Available</th>
                                <th className="text-right py-2 px-3 font-semibold text-gray-600">Can Dispatch</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...o.items].sort((a, b) => {
                                // Green (fully ready) first, then orange (partial), then red (no stock)
                                const score = (item) => item.is_fully_ready ? 0 : item.has_any_stock ? 1 : 2;
                                return score(a) - score(b);
                              }).map((item, iIdx) => {
                                const sorted = sortVariants(item.variants);
                                const firstImg = (item.images || [])[0];
                                return sorted.map((v, vIdx) => (
                                  <tr key={`${iIdx}-${vIdx}`} className={`border-b border-gray-100 ${vIdx === 0 && iIdx > 0 ? 'border-t-[3px] border-t-gray-400' : ''}`}>
                                    {vIdx === 0 && (
                                      <td className="py-1 px-2 text-center align-middle" rowSpan={sorted.length}>
                                        {firstImg ? (
                                          <img src={`${API_BASE}${firstImg}`} alt={item.design_code}
                                            className="w-10 h-10 rounded object-cover cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all mx-auto"
                                            onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)} />
                                        ) : (
                                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto text-[9px]">No img</div>
                                        )}
                                      </td>
                                    )}
                                    <td className={`py-2 px-3 font-mono font-semibold ${vIdx === 0 ? '' : 'text-gray-400'}`}>
                                      <div className="flex items-center gap-2">
                                        {item.design_code}
                                        {vIdx === 0 && item.is_fully_ready && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Fully ready" />}
                                        {vIdx === 0 && !item.is_fully_ready && item.has_any_stock && <span className="inline-block w-2 h-2 rounded-full bg-amber-400" title="Partial stock" />}
                                        {vIdx === 0 && !item.has_any_stock && <span className="inline-block w-2 h-2 rounded-full bg-red-400" title="No stock" />}
                                      </div>
                                    </td>
                                    <td className="py-2 px-3">{v.size}</td>
                                    <td className="py-2 px-3">{v.color}</td>
                                    <td className="py-2 px-3 text-right">{v.qty_ordered}</td>
                                    <td className="py-2 px-3 text-right text-gray-400">{v.qty_dispatched}</td>
                                    <td className="py-2 px-3 text-right font-medium">{v.remaining}</td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={`font-medium ${v.available_stock >= v.remaining ? 'text-emerald-600' : v.available_stock > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                                        {v.available_stock}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={`font-bold ${v.can_dispatch >= v.remaining ? 'text-emerald-600' : v.can_dispatch > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                                        {v.can_dispatch}
                                      </span>
                                    </td>
                                  </tr>
                                ));
                              })}
                            </tbody>
                          </table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

    </div>

    {/* Image Lightbox */}
    {lightboxImg && (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setLightboxImg(null)}>
        <img src={lightboxImg} alt="Design" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" />
        <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-xl"
          onClick={() => setLightboxImg(null)}>✕</button>
      </div>
    )}
    </>
  );
};

export default ReadyToDispatch;
